import { formatBytes } from "./formatting";

export const unavailable = "n/a";

const isNumber = (value) =>
  typeof value === "number" && Number.isFinite(value);

const valueOrUnavailable = (value) =>
  value === undefined || value === null ||
  (typeof value === "number" && !Number.isFinite(value))
    ? unavailable
    : value;

const bytesOrUnavailable = (value) =>
  isNumber(value) ? formatBytes(value) : unavailable;

const groupedValues = (values) =>
  values.every((value) => value === unavailable)
    ? unavailable
    : values.join(" / ");

export function effectiveCpu(snapshot) {
  if (snapshot.share_factor) {
    return isNumber(snapshot.total_cpu) && isNumber(snapshot.share_factor)
      ? snapshot.total_cpu * snapshot.share_factor
      : null;
  }

  return isNumber(snapshot.total_cpu) ? snapshot.total_cpu : null;
}

export function loadUsage(cpu, load) {
  if (!isNumber(load)) {
    return null;
  }

  return {
    load,
    free: isNumber(cpu) ? (cpu - load).toFixed(2) : null,
  };
}

export function memoryUsage(snapshot) {
  if (
    !isNumber(snapshot.total_memory) ||
    !isNumber(snapshot.min_memory_available_gb) ||
    snapshot.total_memory <= 0
  ) {
    return null;
  }

  const used = snapshot.total_memory - snapshot.min_memory_available_gb;
  return {
    total: snapshot.total_memory,
    used: used.toFixed(2),
    percentage: ((used / snapshot.total_memory) * 100).toFixed(2),
  };
}

export function snapshotDetails(snapshot) {
  const maxMemoryUsed =
    isNumber(snapshot.total_memory) &&
    isNumber(snapshot.min_memory_available_gb)
      ? formatBytes(snapshot.total_memory - snapshot.min_memory_available_gb)
      : unavailable;

  return {
    Spec: {
      "Total RAM": bytesOrUnavailable(snapshot.total_memory),
      "Total vCPU": effectiveCpu(snapshot) ?? unavailable,
    },
    Load: {
      "Max 1m load average": valueOrUnavailable(snapshot.max_load_avg1),
      "Max 5m load average": valueOrUnavailable(snapshot.max_load_avg5),
      "Max 15m load average": valueOrUnavailable(snapshot.max_load_avg15),
    },
    Memory: {
      "Max RAM usage": maxMemoryUsed,
      "Max 5m avg RAM": bytesOrUnavailable(snapshot.max_memory_avg5),
    },
    Disk: {
      "Disk (read/write)": groupedValues([
        bytesOrUnavailable(snapshot.disk_read_total),
        bytesOrUnavailable(snapshot.disk_write_total),
      ]),
      "Space (free/used/total)": groupedValues([
        bytesOrUnavailable(snapshot.disk_space_free),
        bytesOrUnavailable(snapshot.disk_space_used),
        bytesOrUnavailable(snapshot.disk_space_total),
      ]),
      "Max I/O inflight": valueOrUnavailable(snapshot.max_disk_io_inflight),
    },
    Network: {
      "RX/TX": groupedValues([
        bytesOrUnavailable(snapshot.network_read_total),
        bytesOrUnavailable(snapshot.network_write_total),
      ]),
    },
    System: {
      "Entropy (min/max)": groupedValues([
        valueOrUnavailable(snapshot.min_entropy),
        valueOrUnavailable(snapshot.max_entropy),
      ]),
      "Max open files": valueOrUnavailable(snapshot.max_open_files),
      "Max open connection": valueOrUnavailable(snapshot.max_open_connections),
      "Processes since boot": valueOrUnavailable(snapshot.processes_since_boot),
    },
  };
}

export function diskUsage(snapshot) {
  if (
    !isNumber(snapshot.disk_space_total) ||
    !isNumber(snapshot.disk_space_free) ||
    snapshot.disk_space_total <= 0
  ) {
    return null;
  }

  const used = snapshot.disk_space_total - snapshot.disk_space_free;
  return {
    total: snapshot.disk_space_total,
    used: used.toFixed(2),
    percentage: ((used / snapshot.disk_space_total) * 100).toFixed(2),
  };
}

export function compareSnapshotAgents(a, b, order) {
  const aAgent = a.agent_name || a.agentName || "";
  const bAgent = b.agent_name || b.agentName || "";

  if (!aAgent && !bAgent) {
    return 0;
  }
  if (!aAgent) {
    return 1;
  }
  if (!bAgent) {
    return -1;
  }

  const options = { sensitivity: "base" };
  return order === "desc"
    ? aAgent.localeCompare(bAgent, undefined, options)
    : bAgent.localeCompare(aAgent, undefined, options);
}
